import { DEDUCTION, NONE_DEDUCTION } from "../../constants/deduction";

// カート行・お気に入り行の「同じ商品か」の定義と、行データの組み立てを集約する純関数群。
//
// 社内品番(idin)と社外品番(idout)は一意ではない。色違いや股下長タイプは社外品番を共有し、
// 社内品番まで同じで購入先と金額が違う別商品(390・392)もある。
// かつて idout + size で判定していたため別商品が1行に合算された(2026-11の問い合わせ)。
// 商品ドキュメントID(productId)だけが商品を一意に特定できる。

// 同一判定に使うフィールド(唯一の定義元)
export const CART_LINE_IDENTITY_FIELDS = ["productId", "size"];

// 同じ商品・同じサイズなら必ず同じIDになる、カート行のドキュメントID。
// productId(商品ドキュメントID)は空でない前提のため、Firestoreが禁止する `__.*__` には一致しない。
export const buildCartLineId = (item) =>
  CART_LINE_IDENTITY_FIELDS.map((field) =>
    encodeURIComponent(String(item[field]))
  ).join("__");

const compareStrings = (a, b) => (a < b ? -1 : a > b ? 1 : 0);

// 商品IDを使う行IDへ変更する前のドキュメントID順を再現するためのキー。
// 現在の形式の行は当時の形式(社外品番+サイズ)に変換し、それより前に作られた行は今のIDをそのまま使う。
const buildCartLineSortKey = (line) =>
  line.cartId === buildCartLineId(line)
    ? `${encodeURIComponent(String(line.idout))}__${encodeURIComponent(
        String(line.size)
      )}`
    : line.cartId;

// カート行を表示・控除計算の順に並べる。
// 控除枠は行の順に消費されるため(getUpdatedDeductionProducts)、並び順は本人負担額と注文の可否を左右する。
// 行IDの形式に左右されないよう、ここで順序を明示する。
//
// 社外品番はこの順序の再現にだけ使い、同一判定には使わない。
// キーが同じ行(以前は合算されていた、社外品番とサイズも同じ組)は、cartIdの順にする。
export const sortCartLines = (lines) =>
  [...lines].sort(
    (a, b) =>
      compareStrings(buildCartLineSortKey(a), buildCartLineSortKey(b)) ||
      compareStrings(a.cartId, b.cartId)
  );

// 行データのスキーマ定義。カート・お気に入りの全経路がここを通る
const buildLine = ({ source, price, quantity, size, remarks, addedAt, names }) => {
  const unitPrice = Number(price);
  const lineQuantity = Number(quantity);
  const totalPrice = unitPrice * lineQuantity;
  return {
    added_at: addedAt, //追加日
    idin: source.idin, //社内番号
    idout: source.idout, //社外番号
    description: source.description, //商品詳細
    images: source.images, //画像パス
    name: source.name, //商品名
    price: unitPrice, //価格
    productId: names.productId, //ID
    quantity: lineQuantity, //数量
    size, //サイズ
    category: source.category, //カテゴリID
    category_name: names.category_name, //カテゴリ名
    maker: source.maker, //メーカーID
    maker_name: names.maker_name, //メーカー名
    shop: source.shop, //販売店ID
    shop_name: names.shop_name, //販売店名
    shop_fax: names.shop_fax, //販売店FAX
    shop_to: names.shop_to, //販売店email TO
    shop_cc: names.shop_cc, //販売店email CC
    totalPrice, //合計金額
    deduction_price: totalPrice, //控除金額
    deduction_id: NONE_DEDUCTION, //控除区分
    deduction_name: DEDUCTION.NONE_DEDUCTION, //控除区分名
    deduction_is_valid: true,
    deduction_correct_count: lineQuantity, //控除区分が適用できる数量
    embroidery: source.embroidery, //刺繍
    remarks, //備考
    ordernumber: -1,
  };
};

// 商品マスタとサイズ選択から行データを作る(商品詳細画面のカート・お気に入り追加)
export const buildLineFromProduct = ({
  product,
  selectedItem,
  shops,
  makers,
  categories,
  addedAt,
}) => {
  const shop = shops.find((candidate) => candidate.sid === product.shop);
  const maker = makers.find((candidate) => candidate.mid === product.maker);
  const category = categories.find(
    (candidate) => candidate.cid === product.category
  );
  return buildLine({
    source: product,
    price: selectedItem.basePrice,
    quantity: selectedItem.quantity,
    size: selectedItem.size,
    remarks: "",
    addedAt,
    names: {
      productId: product.id,
      category_name: category?.name,
      maker_name: maker?.name,
      shop_name: shop?.name,
      shop_fax: shop?.fax,
      shop_to: shop?.to,
      shop_cc: shop?.cc,
    },
  });
};

// お気に入り行からカート行データを作る(お気に入り画面のカート追加)
export const buildLineFromFavorite = ({
  favorite,
  quantity,
  remarks,
  addedAt,
}) =>
  buildLine({
    source: favorite,
    price: favorite.price,
    quantity,
    size: favorite.size,
    remarks,
    addedAt,
    names: {
      productId: favorite.productId,
      category_name: favorite.category_name,
      maker_name: favorite.maker_name,
      shop_name: favorite.shop_name,
      shop_fax: favorite.shop_fax,
      shop_to: favorite.shop_to,
      shop_cc: favorite.shop_cc,
    },
  });

// 社内品番の表示文字列。空欄の商品(裾上げ加工代など)は空文字を返す
export const formatInternalCode = (idin) => {
  const code = String(idin ?? "").trim();
  return code ? `社内品番 ${code}` : "";
};
